import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmService, PagedResult, ProductTypeEnum } from '@tymes4-shared';
import { LongRunningOperationModel, LongRunningOperationService, OrderLineViewWithPricing } from '../../api';
import { first } from 'rxjs/operators';

export interface BulkCreditDialogData {
  title: string;
  orderLines: OrderLineViewWithPricing[];
  orderId: number;
}

interface BulkCreditOrderLinesContext {
  OrderId: number;
  OrderLineIds: number[];
}

type SelectionMode = 'choose' | 'manual' | 'amount';

@Component({
    selector: 'app-bulk-credit-order-lines',
    templateUrl: './bulk-credit-order-lines.component.html',
    styleUrls: ['./bulk-credit-order-lines.component.scss'],
    standalone: false
})
export class BulkCreditOrderLinesComponent implements OnInit {
  mode: SelectionMode = 'choose';
  creditableLines: OrderLineViewWithPricing[] = [];
  selectedLines: OrderLineViewWithPricing[] = [];
  creditAmount: number = 1;

  pagedLines: OrderLineViewWithPricing[] = [];
  currentPage = 1;
  pageSize = 10;

  @ViewChild('listVw') listVw;

  public listOptions = {
    search: false,
    pageSize: this.pageSize,
    fixedTableLayout: true,
    disableSort: true,
    renderInCard: false,
    noSpacing: true,
    headers: [
      {sortField: null, label: '', width: 60, hideOn: null},
      {sortField: null, label: 'T4.ORDERDETAILS.HEADER.PRODUCT', hideOn: null},
      {sortField: null, label: 'T4.ORDERDETAILS.HEADER.SECTIONROWSEAT', width: 150, hideOn: null},
      {sortField: null, label: 'T4.ORDERDETAILS.HEADER.TYPE', width: 150, hideOn: null},
      {sortField: null, label: 'T4.ORDERDETAILS.HEADER.INTHENAMEOF', width: 190, hideOn: null},
      {sortField: null, label: 'T4.ORDERDETAILS.HEADER.TOTAL', width: 80, hideOn: null, class: 'text-end' },
    ]
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: BulkCreditDialogData,
    public dialogRef: MatDialogRef<BulkCreditOrderLinesComponent>,
    private translate: TranslateService,
    private confirmService: ConfirmService,
    private lroService: LongRunningOperationService
  ) {}

  ngOnInit(): void {
    this.creditableLines = this.data.orderLines.filter(ol => 
      !ol.IsCredited && (
        ol.ProductType === ProductTypeEnum.Ticket || 
        ol.ProductType === ProductTypeEnum.Membership || 
        ol.ProductType === ProductTypeEnum.CrossSellProduct || 
        ol.ProductType === ProductTypeEnum.MatchDayPass
      )
    );
  }

  get totalCreditable(): number {
    return this.creditableLines.length;
  }

  selectMode(mode: SelectionMode): void {
    this.mode = mode;
    this.selectedLines = [];
    this.creditAmount = 1;
  }

  toggleSelection(line: OrderLineViewWithPricing): void {
    if (this.isSelected(line)) {
      const index = this.selectedLines.indexOf(line);
      this.selectedLines.splice(index, 1);
    } else {
      this.selectedLines.push(line);
    }
  }

  isSelected(line: OrderLineViewWithPricing): boolean {
    return this.selectedLines.some(sl => sl.Id === line.Id);
  }

  toggleSelectAll(selectAll: boolean): void {
    if (selectAll) {
      this.selectedLines = [...this.creditableLines];
    } else {
      this.selectedLines = [];
    }
  }

  get allSelected(): boolean {
    return this.selectedLines.length === this.creditableLines.length && this.creditableLines.length > 0;
  }

  onPageChange(pageArgs): void {
    let paged = this.creditableLines.slice((pageArgs.pageNr - 1) * this.pageSize, (pageArgs.pageNr) * this.pageSize);
    
    var d : PagedResult = {
      Records: paged,
      TotalRecords: this.creditableLines.length,
    }

    this.listVw.Data = d;
  }

  get totalPages(): number {
    return Math.ceil(this.creditableLines.length / this.pageSize);
  }

  get maxAmountLines(): number {
    return this.totalCreditable;
  }

  get isAmountValid(): boolean {
    return Number.isInteger(this.creditAmount)
      && this.creditAmount >= 1
      && this.creditAmount <= this.maxAmountLines;
  }

  submit(): void {
    let linesToCredit: OrderLineViewWithPricing[];

    if (this.mode === 'manual') {
      linesToCredit = this.selectedLines;
    } else {
      const sourceLines = this.creditableLines;
      linesToCredit = sourceLines.slice(-this.creditAmount);
    }

    if (linesToCredit.length === 0) {
      return;
    }

    const count = linesToCredit.length;
    this.confirmService.confirm({
      title: this.translate.instant('SALES.ORDERS.DETAILS.DIALOG.BULK-CREDIT.CONFIRM.TITLE'),
      message: this.translate.instant('SALES.ORDERS.DETAILS.DIALOG.BULK-CREDIT.CONFIRM.MSG', { count })
    }).subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      const context: BulkCreditOrderLinesContext = {
        OrderId: this.data.orderId,
        OrderLineIds: linesToCredit.map(l => l.Id)
      };

      this.lroService.registerBulkCreditOrderLines(context)
        .pipe(first())
        .subscribe((lro: LongRunningOperationModel) => {
          this.dialogRef.close(lro);
        });
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
